import DPO from 'dpo8core';
import { isDesignIn, isArtworkInfoEnabled } from '@app/utils/utils';
import { getMailMergeHintByKey, getFirstTransparencyCode, getLastTransparencyCode } from '@app/utils/mailMerge';
import { fromTwips, getLocale } from '@app/utils/measurement';
import { clearProjectFields } from '@app/utils/editor';
import { showFinishedArtworkInfo, addError } from '@app/actions/popupsActions';
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import { fetchArtwork } from '@app/actions/uploadActions';
import endpoint from '@app/endpoint';
import {
  STATE_ARTWORK_PRODUCT_DATA,
  STATE_ARTWORK_INFO,
  STATE_ARTWORK_IMAGE,
  STATE_ARTWORK_PROJECT,
  STATE_ARTWORK_PAPER_MATERIAL,
  RESET_ARTWORK_INFO,
  RESET_ARTWORK_IMAGE,
  RENAME_DESIGN,
} from '@app/actions/_types';

export const resetArtworkInfo = () => ({
  type: RESET_ARTWORK_INFO,
});

export const stateArtworkInfo = artworkInfo => ({
  type: STATE_ARTWORK_INFO,
  payload: artworkInfo,
});

export const resetArtworkImage = () => ({
  type: RESET_ARTWORK_IMAGE,
});

export const stateArtworkImage = artworkImage => ({
  type: STATE_ARTWORK_IMAGE,
  payload: artworkImage,
});

export const stateArtworkPaperMaterial = artworkPaperMaterial => ({
  type: STATE_ARTWORK_PAPER_MATERIAL,
  payload: artworkPaperMaterial,
});

export const stateArtworkProject = artworkProject => ({
  type: STATE_ARTWORK_PROJECT,
  payload: artworkProject,
});

export const stateArtworkProductData = productData => ({
  type: STATE_ARTWORK_PRODUCT_DATA,
  payload: productData,
});

export const renameDesign = name => ({
  type: RENAME_DESIGN,
  payload: name,
});

export const sendFinishedArtworkInfo = artworkInfo => {
  window.top.postMessage(artworkInfo, '*');
  window.close();
};

export const createArtworkProject = (params, shapeData) => dispatch =>
  new Promise((resolve, reject) => {
    dispatch(startLoading());
    endpoint.createCustomSizeProject(
      params,
      shapeData,
      ({ data: projectData }) => {
        dispatch(stateArtworkProject(projectData));
        dispatch(endLoading());
        resolve(projectData);
      },
      error => {
        dispatch(endLoading());
        reject(error);
      },
      this,
    );
  });

  export const fetchProductData = (params) => dispatch =>
  new Promise((resolve, reject) => {
    endpoint.fetchProductsData(
      params,
      ({ data: productData = {} }) => {
        dispatch(stateArtworkProductData(productData));
        resolve(productData);
      },
      error => {
        reject(error);
      },
      this,
    );
  });

export const renameArtwork = () => (dispatch, getState) =>
  new Promise(resolve => {
    const {
      artwork: {
        info: { artwork_id, designName },
      },
    } = getState();

    dispatch(startLoading());
    endpoint.renameArtwork(
      artwork_id,
      designName,
      ({ data: { name } }) => {
        dispatch(
          stateArtworkInfo({
            name: name,
            designName: name,
          }),
        );
        dispatch(endLoading());
        resolve();
      },
      error => {
        console.error(error);
        dispatch(endLoading());
      },
      this,
    );
  });

export const getStatusWarnings = artworkStatus => {
  const {
    dto: { ErrorType: errorTypes, ErrorCode: errorCodes },
  } = DPO;

  const statusWarningsMessage = {
    WRONG_DIELINE: 'Dieline technical ink found but unusable or wrong file type.',
    WITH_DIELINE_NO_VDP: 'Spot color for VDP barcode not included in art. Requires review.',
    MISSING_VDP: 'Spot color for VDP barcode not included in art. Requires review.',
    SPOT_COLORS: 'Other spot colors found in art. Requires review.',
    NON_READABLE: 'Artwork not readable. Requires review.',
    CUSTOM_SHAPE: 'Custom shape artwork. Requires review. ',
    CUSTOM_SIZE: 'Custom size artwork. Requires review. ',
  };

  const statusHasWarningMessage = Object.hasOwnProperty.call(errorCodes, artworkStatus);
  const statusHasErrorCode = Object.hasOwnProperty.call(statusWarningsMessage, artworkStatus);

  return statusHasErrorCode && statusHasWarningMessage
    ? [
        {
          errorType: errorTypes.WARNING,
          errorCode: errorCodes[artworkStatus],
          errorMessage: statusWarningsMessage[artworkStatus],
        },
      ]
    : [];
};

export const getProjectWarnings = (flow, context, artworkStatus) => {
  const { projectData: { projectWarnings: warnings } = {} } = flow;

  const errors = context.getProjectWarnings();
  const statusWarnings = getStatusWarnings(artworkStatus);
  const projectWarnings = (warnings || []).concat(errors, statusWarnings);

  return projectWarnings;
};

export const getCheckDesignCodes = flowConfig => {
  const { dto: { ErrorCode: dtoErrors } = {} } = DPO;

  const featureCheckDesignStatus = flowConfig.features.getFeature('checkDesignStatus');
  const checkDesignStatuses = featureCheckDesignStatus?.getAllParamValues() || [];
  const checkDesingCodes = checkDesignStatuses.map(checkDesignStatus => dtoErrors[checkDesignStatus]);

  return checkDesingCodes;
};

export const getDesignInCodesAbsenceError = flow => {
  const { projectData: { mailMergeData } = {} } = flow;
  const {
    dto: {
      ErrorType: { ERROR },
      ErrorCode: { MISSING_CODES },
    },
  } = DPO;

  const codesMissing = !mailMergeData || !getFirstTransparencyCode(mailMergeData);

  return codesMissing ? { errorType: ERROR, errorCode: MISSING_CODES } : null;
};

export const getCheckDesignFlag = (flow, context, flowConfig, artworkStatus) => {
  const checkDesignCodes = getCheckDesignCodes(flowConfig);
  const projectWarnings = getProjectWarnings(flow, context, artworkStatus);

  if (isDesignIn(flowConfig)) {
    const missingCodesError = getDesignInCodesAbsenceError(flow);
    missingCodesError && projectWarnings.push(missingCodesError);
  }

  return projectWarnings.some(({ errorCode }) => checkDesignCodes.includes(errorCode));
};

export const getCheckDesignReason = (flowConfig, projectWarnings, artworkStatus) => {
  const errorTypeName = errorType => {
    const { dto: { ErrorType: errorTypes } = {} } = DPO;
    return Object.keys(errorTypes).find(type => Number(errorTypes[type]) === Number(errorType));
  };

  const statusWarnings = getStatusWarnings(artworkStatus);
  const checkDesignCodes = getCheckDesignCodes(flowConfig);

  const checkDesignStatusWarnings = statusWarnings.filter(({ errorCode }) => checkDesignCodes.includes(errorCode));
  const warningsWithMessage = [...projectWarnings, ...checkDesignStatusWarnings].filter(({ errorMessage }) => !!errorMessage);

  const checkDesignReason = warningsWithMessage
    .map(({ errorCode, errorMessage, errorType }) => `(${errorCode}) ${errorTypeName(errorType)}: ${errorMessage}`)
    .join('; ');

  return checkDesignReason;
};

export const getArtworkPanelDimensions = (flow, flowConfig) => {
  const {
    project: {
      masterPanels: [masterPanel],
    },
  } = flow;

  const {
    locale: { measurementSystem: localMeasurement },
  } = flowConfig;

  const width = fromTwips(masterPanel.width, getLocale(localMeasurement));
  const height = fromTwips(masterPanel.height, getLocale(localMeasurement));

  return { width, height };
};

export const processFinishedArtwork = (artworkData, flow, flowConfig) => (dispatch, getState) => {
  let { width: panel_width, height: panel_height } = getArtworkPanelDimensions(flow, flowConfig);
  const { quote: {
    material,
    labelHeight,
    labelWidth,
    cuttingType,
    addWhiteColor,
    impositionCount,
    includesWhiteLayer,
    includesWhiteUnderprint,
  } = {} } = artworkData;
  let {
    matrix: {
      ctx: { shape = '', qty = 0, designName = '', consumer = '' },
    },
    artwork: { info: artworkInfo, project: { product = {} } = {} },
  } = getState();

  if (isDesignIn(flowConfig)) {
    const { mailMergeData } = artworkData;

    const { value: codesNumber = 0 } = getMailMergeHintByKey(mailMergeData, 'numberOfCodes');
    qty = codesNumber;

    const { value: brandId = '' } = getMailMergeHintByKey(mailMergeData, 'brandIdentifier');
    const firstCode = getFirstTransparencyCode(mailMergeData);
    const lastCode = getLastTransparencyCode(mailMergeData);

    Object.assign(artworkInfo, { brandId, firstCode, lastCode });
  }

  delete artworkData.mailMergeData;
  delete artworkInfo.mailMergeData;
  !artworkInfo.proofPdfUrl && delete artworkInfo.proofPdfUrl;
  !artworkData.proofPdfUrl && delete artworkData.proofPdfUrl;

  const { sku, warnings, errors, check_design: checkDesign, artwork_status: artworkStatus } = artworkData;

  const projectWarns = [...warnings, ...errors];
  const resized = sku && product && product.sku && product.sku !== sku ? 'Y' : 'N';
  const checkDesignReason = checkDesign.toUpperCase() === 'Y' ? getCheckDesignReason(flowConfig, projectWarns, artworkStatus) : '';
  const doShowArtworkInfo = isArtworkInfoEnabled(flowConfig);

  panel_height = labelHeight || panel_height;
  panel_width = labelWidth || panel_width;

  const finishedArtworkInfo = {
    ...artworkInfo,
    ...artworkData,
    qty: Number(qty),
    checkDesignReason,
    panel_height,
    panel_width,
    designName,
    resized,
    shape,
    addWhiteColor,
    includesWhiteLayer,
    includesWhiteUnderprint,
    material,
    cuttingType,
    impositionCount,
  };

  doShowArtworkInfo ? dispatch(showFinishedArtworkInfo(finishedArtworkInfo)) : sendFinishedArtworkInfo(finishedArtworkInfo);
};

export const finishArtwork = (flow, context, flowConfig, clearFields = false) => async (dispatch, getState) => {
  const {
    matrix: {
      ctx: { artworkId: ctxArtworkId },
    },
    artwork: { info: artworkInfo },
  } = getState();

  if (!artworkInfo.artwork_id && ctxArtworkId) {
    const { data: fetchedArtworkInfo } = await fetchArtwork(ctxArtworkId);
    Object.assign(artworkInfo, fetchedArtworkInfo);
  }

  if (clearFields) {
    clearProjectFields(flow);
  }

  return new Promise(resolve => {
    dispatch(startLoading());

    const { artwork_id: artworkId, artwork_status: artworkStatus } = artworkInfo;
    const checkDesign = context.project && getCheckDesignFlag(flow, context, flowConfig, artworkStatus);

    endpoint.finishArtworkProject(
      artworkId,
      checkDesign,
      flow,
      ({ data = {} }) => {
        dispatch(endLoading());
        resolve(data);
      },
      () => dispatch(endLoading()),
      this,
    );
  });
};

export const createContext = (flow, context, project) => dispatch =>
  new Promise(resolve => {
    const { projectData, paperMaterial } = flow;
    const {
      id: projectId,
      product: { productProfile },
    } = projectData;

    const provider = new DPO.mmd.DataSourceProvider();
    const newContext = context.createNew(project, projectData, provider, null, null, paperMaterial);

    newContext.storage.imageType = productProfile === 'DEFAULT' ? 'low' : 'cmyk';
    newContext.userChoices = {
      backSideViewed: !!projectId,
      backSideChosen: !!projectId || project.designTheme === 'Blank',
    };
    newContext.projectSaveStatus = {
      savedToComputer: false,
    };

    dispatch(stateArtworkPaperMaterial(paperMaterial));
    dispatch(stateArtworkProject(projectData));
    flow.provider = newContext.provider;
    flow.chain.clearData();
    resolve(newContext);
  });

export const processError = (sender, errors) => dispatch => {
  const [error = {}] = errors;
  const { code, message } = error;
  const group = code - (code % 100);

  let errorLocKey = 'qau-error-';
  switch (group) {
    case 300:
      errorLocKey += '300';
      break;
    case 400:
      errorLocKey += code;
      break;
    default:
      errorLocKey += '500';
      break;
  }

  dispatch(addError(errorLocKey, { message }));
};

export const addProjectWarning = ({ projectData }, code) => {
  const { projectWarnings } = projectData;
  const {
    dto: { ErrorType, ErrorCode },
  } = DPO;

  projectData.projectWarnings = projectWarnings || [];
  projectData.projectWarnings.push({
    errorType: ErrorType.WARNING,
    errorCode: ErrorCode[code],
    errorMessage: '',
  });
};

export const removeProjectWarning = ({ projectData }, code) => {
  const { projectWarnings = [] } = projectData;
  const {
    dto: { ErrorCode },
  } = DPO;

  const updatedProjectWarnings = projectWarnings ? projectWarnings.filter(({ errorCode }) => errorCode !== ErrorCode[code]) : [];

  projectData.projectWarnings = updatedProjectWarnings;
};
